/**
 * ProposalTerms — ESA or CapEx agreement terms card
 *
 * ESA: Rate model, escalator, term, $0 upfront
 * CapEx: System cost, ITC credit, net cost, payback period
 */

import React from 'react';
import { Box, Typography, Divider, TextField, InputAdornment } from '@mui/material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { formatCurrency, formatPercent } from './extractProposalData';

export default function ProposalTerms({ data, hideTitle, onDiscountRateChange }) {
  if (data.mode === 'esa') {
    return <EsaTerms data={data} hideTitle={hideTitle} onDiscountRateChange={onDiscountRateChange} />;
  }
  return <CapexTerms data={data} hideTitle={hideTitle} onDiscountRateChange={onDiscountRateChange} />;
}

function EsaTerms({ data, hideTitle, onDiscountRateChange }) {
  const { esaTerms, effectiveRate, rateDiscount, npv, assumptions, vpp } = data;
  if (!esaTerms) return null;

  const modelLabels = {
    fixed_rate: 'Fixed Rate',
    savings_share: 'Savings Share',
    fixed_monthly: 'Fixed Monthly',
    capacity_payment: 'Capacity Payment',
  };

  const rows = [
    { label: 'Agreement Type', value: 'Energy Service Agreement (ESA)' },
    { label: 'Pricing Model', value: modelLabels[esaTerms.model] || esaTerms.model },
    { label: 'Year 1 Payment', value: esaTerms.paymentDescription || formatCurrency(esaTerms.year1Payment) },
    { label: 'Annual Escalation', value: esaTerms.escalation != null ? formatPercent(esaTerms.escalation) : '—' },
    { label: 'Term', value: esaTerms.term ? `${esaTerms.term} years` : '—' },
    { label: 'Upfront Cost', value: '$0', highlight: true },
  ];

  if (effectiveRate != null && effectiveRate > 0) {
    rows.push({ label: 'Effective Rate', value: `$${effectiveRate.toFixed(3)}/kWh` });
  }
  if (rateDiscount != null && rateDiscount > 0) {
    rows.push({ label: 'Rate Discount vs Current', value: formatPercent(rateDiscount), highlight: true });
  }
  if (vpp?.included && vpp.annualRevenue > 0) {
    rows.push({ label: 'VPP Revenue (Annual)', value: formatCurrency(vpp.annualRevenue), highlight: true });
    if (vpp.lifetimeRevenue > 0) {
      rows.push({ label: 'Lifetime VPP Revenue', value: formatCurrency(vpp.lifetimeRevenue) });
    }
    if (vpp.durationYears) {
      rows.push({ label: 'VPP Duration', value: `${vpp.durationYears} years` });
    }
  }
  if (assumptions?.discountRate != null) {
    rows.push({ label: 'Discount Rate (WACC)', editable: true, editKey: 'discountRate', value: assumptions.discountRate, unit: '%' });
  }
  if (npv != null) {
    rows.push({ label: 'Net Present Value (NPV)', value: formatCurrency(npv), bold: true, highlight: npv >= 0 });
  }

  return <TermsCard title="Financials & Commercial" rows={rows} hideTitle={hideTitle} onEditableChange={onDiscountRateChange} />;
}

function CapexTerms({ data, hideTitle, onDiscountRateChange }) {
  const { capexTerms, year1OM, npv, assumptions, vpp } = data;
  if (!capexTerms) return null;

  const rows = [
    { label: 'Agreement Type', value: 'Direct Purchase' },
    { label: 'System Price', value: formatCurrency(capexTerms.salePrice) },
    { label: 'Federal ITC (30%)', value: capexTerms.itcCredit > 0 ? `(${formatCurrency(capexTerms.itcCredit)})` : 'Not eligible', highlight: capexTerms.itcCredit > 0 },
    { label: 'Net Cost', value: formatCurrency(capexTerms.netCost), bold: true },
    { label: 'Simple Payback', value: capexTerms.paybackYears != null ? `${capexTerms.paybackYears.toFixed(1)} years` : '—' },
    { label: 'Ownership Term', value: `${capexTerms.term} years` },
  ];

  if (year1OM != null) {
    rows.push({ label: 'Est. Year 1 O&M', value: formatCurrency(year1OM) });
  }
  if (vpp?.included && vpp.annualRevenue > 0) {
    rows.push({ label: 'VPP Revenue (Annual)', value: formatCurrency(vpp.annualRevenue), highlight: true });
    if (vpp.lifetimeRevenue > 0) {
      rows.push({ label: 'Lifetime VPP Revenue', value: formatCurrency(vpp.lifetimeRevenue) });
    }
    if (vpp.durationYears) {
      rows.push({ label: 'VPP Duration', value: `${vpp.durationYears} years` });
    }
  }
  if (assumptions?.discountRate != null) {
    rows.push({ label: 'Discount Rate (WACC)', editable: true, editKey: 'discountRate', value: assumptions.discountRate, unit: '%' });
  }
  if (npv != null) {
    rows.push({ label: 'Net Present Value (NPV)', value: formatCurrency(npv), bold: true, highlight: npv >= 0 });
  }

  return <TermsCard title="Financials & Commercial" rows={rows} hideTitle={hideTitle} onEditableChange={onDiscountRateChange} />;
}

function TermsCard({ title, rows, hideTitle, onEditableChange }) {
  return (
    <Box>
      {!hideTitle && (
        <Typography
          variant="overline"
          sx={{
            color: vortxColors.stone,
            fontWeight: 600,
            letterSpacing: '0.1em',
            mb: 2,
            display: 'block',
          }}
        >
          {title}
        </Typography>
      )}

      <Box
        sx={{
          borderRadius: 2,
          border: `1px solid rgba(255,255,255,0.08)`,
          bgcolor: 'rgba(255,255,255,0.03)',
          overflow: 'hidden',
        }}
      >
        {rows.map((row, i) => (
          <React.Fragment key={row.label}>
            {i > 0 && (
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2.5,
                py: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: vortxColors.stone }}
              >
                {row.label}
              </Typography>
              {row.editable ? (
                <TextField
                  type="number"
                  size="small"
                  value={row.value}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && onEditableChange) onEditableChange(row.editKey, v);
                  }}
                  onFocus={(e) => e.target.select()}
                  inputProps={{
                    step: 0.5,
                    min: 0,
                    max: 25,
                    style: {
                      fontFamily: vortxTypography.mono,
                      fontSize: '0.85rem',
                      color: vortxColors.white,
                      padding: '4px 8px',
                      textAlign: 'right',
                      width: 50,
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ color: vortxColors.stone, fontSize: '0.75rem' }}>{row.unit}</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                      '&:hover fieldset': { borderColor: vortxColors.gold },
                      '&.Mui-focused fieldset': { borderColor: vortxColors.gold },
                    },
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    fontFamily: vortxTypography.mono,
                    fontWeight: row.bold ? 700 : 500,
                    fontSize: '0.9rem',
                    color: row.highlight ? vortxColors.gold : vortxColors.white,
                  }}
                >
                  {row.value}
                </Typography>
              )}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
